import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { fetchPlayerSummary, type PlayerSummary } from "../utils/homeApi";
import SinglesView from "./SinglesView";
import DoublesView from "./DoublesView";

type Discipline = "singiel" | "debel";

const DISCIPLINE_VALUES: Discipline[] = ["singiel", "debel"];

function parseDiscipline(value: string | null): Discipline | null {
  if (value && DISCIPLINE_VALUES.includes(value as Discipline)) {
    return value as Discipline;
  }

  return null;
}

const PlayerDetails: React.FC = () => {
  const { playerId } = useParams<{ playerId: string }>();
  const playerIdNum = Number(playerId);
  const [searchParams, setSearchParams] = useSearchParams();
  const [summary, setSummary] = useState<PlayerSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const urlTab = parseDiscipline(searchParams.get("tab"));

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchPlayerSummary(playerIdNum)
      .then((s) => {
        if (alive) {
          setSummary(s);
        }
      })
      .catch((error) => {
        console.error("Error loading player summary:", error);
      })
      .finally(() => {
        if (alive) {
          setLoading(false);
        }
      });

    return () => {
      alive = false;
    };
  }, [playerIdNum]);

  const playerName = useMemo(() => {
    if (!summary) {
      return "";
    }

    if (summary.player) {
      return `${summary.player.firstName} ${summary.player.lastName}`.trim();
    }

    return summary.singles?.name ?? summary.doubles?.name ?? "";
  }, [summary]);

  const defaultDiscipline: Discipline = useMemo(() => {
    if (!summary) {
      return "singiel";
    }

    if (!summary.singles && summary.doubles) {
      return "debel";
    }

    return "singiel";
  }, [summary]);

  const discipline: Discipline = urlTab ?? defaultDiscipline;

  const setDiscipline = (next: Discipline): void => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", next);
    setSearchParams(params, { replace: true });
  };

  if (loading) {
    return (
      <div className="card">
        <h2>Wczytywanie…</h2>
      </div>
    );
  }

  if (!summary || (!summary.singles && !summary.doubles)) {
    return (
      <div className="card">
        <h2>Nie znaleziono zawodnika</h2>
        <p>Nie udało się znaleźć wskazanego zawodnika.</p>
        <Link to="/players" className="player-link">
          Powrót do listy zawodników
        </Link>
      </div>
    );
  }

  const hasSingles = !!summary.singles;
  const hasDoubles = !!summary.doubles;

  return (
    <div>
      <div className="card">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1rem",
            flexWrap: "wrap",
            gap: "0.5rem",
          }}
        >
          <h2 style={{ margin: 0 }}>{playerName || "Zawodnik"}</h2>
          <Link
            to={discipline === "debel" ? "/team-players" : "/players"}
            className="player-link"
          >
            ← Powrót do listy zawodników
          </Link>
        </div>

        <div
          role="tablist"
          aria-label="Dyscyplina"
          style={{
            display: "flex",
            gap: "0.5rem",
            borderBottom: "2px solid #dee2e6",
            marginBottom: "1.25rem",
          }}
        >
          <DisciplineTab
            label="Singiel"
            active={discipline === "singiel"}
            available={hasSingles}
            onClick={() => setDiscipline("singiel")}
          />
          <DisciplineTab
            label="Debel"
            active={discipline === "debel"}
            available={hasDoubles}
            onClick={() => setDiscipline("debel")}
          />
        </div>

        {discipline === "singiel" && hasSingles && (
          <SinglesView playerId={playerIdNum} singles={summary.singles!} />
        )}
        {discipline === "singiel" && !hasSingles && (
          <EmptyDiscipline label="singla" otherLabel="debel" switchTo={() => setDiscipline("debel")} />
        )}
        {discipline === "debel" && hasDoubles && (
          <DoublesView playerId={playerIdNum} playerName={playerName} doubles={summary.doubles!} />
        )}
        {discipline === "debel" && !hasDoubles && (
          <EmptyDiscipline label="debla" otherLabel="singla" switchTo={() => setDiscipline("singiel")} />
        )}
      </div>
    </div>
  );
};

interface TabProps {
  label: string;
  active: boolean;
  available: boolean;
  onClick: () => void;
}

const DisciplineTab: React.FC<TabProps> = ({ label, active, available, onClick }) => {
  const baseStyle: React.CSSProperties = {
    padding: "0.75rem 1.25rem",
    border: "none",
    background: "transparent",
    borderBottom: active ? "3px solid #2c7be5" : "3px solid transparent",
    fontWeight: active ? 700 : 500,
    fontSize: "1rem",
    color: available ? (active ? "#2c7be5" : "#495057") : "#adb5bd",
    cursor: "pointer",
    transition: "color 120ms",
  };

  return (
    <button
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={baseStyle}
      title={available ? undefined : "Brak danych w tej dyscyplinie"}
    >
      {label}
      {!available && <span style={{ marginLeft: 6, fontSize: "0.8rem" }}>(brak)</span>}
    </button>
  );
};

interface EmptyProps {
  label: string;
  otherLabel: string;
  switchTo: () => void;
}

const EmptyDiscipline: React.FC<EmptyProps> = ({ label, otherLabel, switchTo }) => (
  <div style={{ padding: "2rem 0", textAlign: "center", color: "#6c757d" }}>
    <p>Ten zawodnik nie ma jeszcze danych dla {label}.</p>
    <button onClick={switchTo} className="btn-primary">
      Zobacz {otherLabel}
    </button>
  </div>
);

export default PlayerDetails;
